import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { PaymentRadioItem } from "@aprosoja/containers";
import { RadioGroup, TextField } from "@aprosoja/ui";

import { useSignUpContext } from "@/app/screens/sign-up/context/sign-up.context";
import { paymentStepFormSchema, PaymentStepFormValues } from "./schema";
import usePayment from "./use-payment";
import ControlButtons from "../components/control-buttons";
import ContentWrapper from "../components/content-wrapper";

const PaymentStep = () => {
  const formMethods = useForm<PaymentStepFormValues>({
    resolver: zodResolver(paymentStepFormSchema),
  });
  const { signUpReducer } = useSignUpContext();
  const { onSubmit } = usePayment();

  return (
    <form
      onSubmit={formMethods.handleSubmit(onSubmit)}
      className="flex flex-col w-full gap-size-x8"
    >
      <ContentWrapper
        title="Forma de pagamento"
        description="Escolha a melhor forma de pagamento"
        hasSummary
      >
        <div className="flex flex-col gap-size-x4">
          <Controller
            control={formMethods.control}
            name="paymentType"
            render={({ field }) => (
              <RadioGroup onValueChange={field.onChange}>
                <PaymentRadioItem
                  annualPayment={String(
                    signUpReducer?.calculationResults?.annualPayment ?? ""
                  )}
                  title="Cartão de crédito"
                  value="CREDIT_CARD"
                >
                  {field.value === "CREDIT_CARD" && (
                    <div className="flex flex-col w-full">
                      <Controller
                        control={formMethods.control}
                        name="number"
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            label="Número do cartão"
                            placeholder="0000 0000 0000 0000"
                            mask="creditCard"
                            required
                            errorMessage={error?.message}
                          />
                        )}
                      />
                      <Controller
                        control={formMethods.control}
                        name="holderName"
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            label="Nome completo"
                            placeholder="Nome impresso no cartão"
                            required
                            errorMessage={error?.message}
                          />
                        )}
                      />
                      <div className="flex w-full gap-size-x4">
                        <Controller
                          control={formMethods.control}
                          name="expiringDate"
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              label="Data de vencimento"
                              placeholder="MM/AA"
                              mask="monthYear"
                              required
                              errorMessage={error?.message}
                            />
                          )}
                        />
                        <Controller
                          control={formMethods.control}
                          name="ccv"
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              label="CVV"
                              placeholder="000"
                              mask="number"
                              maxLength={3}
                              required
                              errorMessage={error?.message}
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}
                </PaymentRadioItem>
                <PaymentRadioItem
                  annualPayment={String(
                    signUpReducer?.calculationResults?.annualPayment ?? ""
                  )}
                  title="PIX"
                  value="PIX"
                />
                <PaymentRadioItem
                  annualPayment={String(
                    signUpReducer?.calculationResults?.annualPayment ?? ""
                  )}
                  title="Boleto"
                  value="BOLETO"
                />
              </RadioGroup>
            )}
          />
        </div>
      </ContentWrapper>
      <ControlButtons isLoading={formMethods.formState.isSubmitting} />
    </form>
  );
};

export default PaymentStep;
